import React, { useCallback, useState } from 'react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function FileUpload({ onDataLoaded }) {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const processFile = useCallback((file) => {
    if (!file) return;
    
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      '.xlsx', '.xls', '.csv'
    ];
    
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setError(t('invalidFileType'));
      return;
    }

    setLoading(true);
    setError('');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        
        if (jsonData.length === 0) {
          setError(t('emptyFile'));
          setLoading(false);
          return;
        }

        // Save to localStorage
        const columns = Object.keys(jsonData[0]);
        localStorage.setItem('excelData', JSON.stringify(jsonData));
        localStorage.setItem('excelColumns', JSON.stringify(columns));
        localStorage.setItem('excelFileName', file.name);
        localStorage.setItem('visibleColumns', JSON.stringify(columns));
        
        onDataLoaded(jsonData, columns, file.name);
      } catch (err) {
        setError(t('readError', { message: err.message }));
      }
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);
  }, [onDataLoaded]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  }, [processFile]);

  const handleFileInput = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  }, [processFile]);

  return (
    <div className="upload-screen">
      <div className="upload-container">
        <div className="lang-switcher-wrapper upload-lang">
          <LanguageSwitcher />
        </div>
        <div className="upload-header">
          <div className="upload-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <h1>{t('appTitle')}</h1>
          <p className="upload-subtitle">{t('uploadSubtitle')}</p>
        </div>

        <div
          className={`drop-zone ${dragActive ? 'drag-active' : ''} ${loading ? 'loading' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t('readingFile')}</p>
            </div>
          ) : (
            <>
              <div className="drop-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <p className="drop-text">{t('dropText')}</p>
              <p className="drop-or">{t('dropOr')}</p>
              <label className="file-select-btn">
                {t('selectFile')}
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileInput}
                  hidden
                />
              </label>
              <p className="file-types">{t('supportedFiles')}</p>
            </>
          )}
        </div>

        {error && (
          <div className="upload-error">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {error}
          </div>
        )}

        <div className="upload-features">
          <div className="feature">
            <div className="feature-icon">📊</div>
            <div>
              <strong>{t('featureTableTitle')}</strong>
              <span>{t('featureTableDesc')}</span>
            </div>
          </div>
          <div className="feature">
            <div className="feature-icon">🔍</div>
            <div>
              <strong>{t('featureBarcodeTitle')}</strong>
              <span>{t('featureBarcodeDesc')}</span>
            </div>
          </div>
          <div className="feature">
            <div className="feature-icon">⚙️</div>
            <div>
              <strong>{t('featureSettingsTitle')}</strong>
              <span>{t('featureSettingsDesc')}</span>
            </div>
          </div>
          <div className="feature">
            <div className="feature-icon">💾</div>
            <div>
              <strong>{t('featureAutoSaveTitle')}</strong>
              <span>{t('featureAutoSaveDesc')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
